import { useLogger } from "@/features/core-shared/composables/useLogger";
import {
  allCustomHighlights,
  getAllCustomHighlights,
} from "@/features/experience-highlights/lib/get-all-custom-highlights";
import {
  allPremadeHighlights,
  getAllPremadeHighlights,
} from "@/features/experience-highlights/lib/get-all-premade-highlights";
import { ExperienceTranslationV2, FlowCode, StatusCode, TranslationType } from "@/types/generated/ContentCommandApi";
import { AvailableLanguage } from "@/types/Language";
import { acceptHMRUpdate, defineStore } from "pinia";
import { useFormValidation } from "../composables/useFormValidation";
import { fetchTranslation } from "../lib/fetch-translation";
import { updateTranslation } from "../lib/update-translation";
import { experienceTranslationSchema } from "../schemas";
import { useContentCommandApi } from "@/composables/useContentCommandApi";
import { useExperienceRawApi } from "@/composables/useExperienceRawApi";
import { waitForTimeout } from "@/features/core-shared/utils/promise/wait";
import { distributionContentKeys } from "@/features/experience-shared/utils/distribution-content-keys";
import { updateDelayMs } from "@/features/experience-shared/constants/experience-shared-constants";
import { Snapshot } from "@/types/Snapshots";

type Translation = Omit<ExperienceTranslationV2, "flow_code"> & {
  flow_code: FlowCode | "";
  lastEnglishSnapshotBeforePublication: Snapshot | undefined;
} & allCustomHighlights &
  allPremadeHighlights;

export const useExperienceTranslationStore = defineStore("use-experience-translation-store", () => {
  const translationId = ref<string | null>(null);
  const loaded = ref(false);
  const modified = ref(false);
  const isSaving = ref(false);

  const translation = ref<Translation>({
    experience_id: "",
    flow_code: "",
    status_code: StatusCode.IN_REVIEW,
    language_code: "",
    title: "",
    translation_type: TranslationType.MANUAL,
    custom_highlights: [],
    custom_important_information: [],
    custom_included: [],
    custom_non_included: [],
    premade_highlights: [],
    premade_important_information: [],
    premade_included: [],
    premade_non_included: [],
    lastEnglishSnapshotBeforePublication: undefined,
  });

  const { getMostRecentTranslationSnapshot, getLastSnapshotBeforeDate } = useExperienceRawApi();

  function getSnapshot(languageCode: AvailableLanguage, experienceId: string) {
    return getMostRecentTranslationSnapshot(experienceId, languageCode).then(({ data }) => {
      if (!data?.update_date) {
        return undefined;
      }
      const lastPublishedDate = new Date(data?.update_date);
      return getLastSnapshotBeforeDate(experienceId, lastPublishedDate).then(({ data }) => data);
    });
  }

  const isTranslationPublished = computed(() => translation.value.status_code === "READY");

  const validation = useFormValidation(translation, experienceTranslationSchema);
  const { logError } = useLogger();

  async function loadTranslation(languageCode: AvailableLanguage, experienceId: string) {
    const [translationData, allCustomHighlightsData, allPremadeHighlightsData, lastEnglishSnapshotBeforePublication] =
      await Promise.all([
        fetchTranslation(languageCode, experienceId),
        getAllCustomHighlights(languageCode, experienceId),
        getAllPremadeHighlights(languageCode, experienceId),
        getSnapshot(languageCode, experienceId),
      ]).catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error);
        throw error;
      });

    const { id, ...data } = translationData;

    if (id == null || id.length === 0) {
      throw new Error("Cannot load a translation without id!");
    }

    translation.value = {
      ...data,
      translation_type: data.translation_type || "MANUAL",
      ...allCustomHighlightsData,
      ...allPremadeHighlightsData,
      lastEnglishSnapshotBeforePublication: lastEnglishSnapshotBeforePublication,
    };

    translationId.value = id;
    loaded.value = true;
    modified.value = false;
    validation.runValidation();
  }

  async function saveTranslation(publish = false, customHighlightsOverride?: Partial<allCustomHighlights>) {
    if (translationId.value == null || translationId.value.length === 0) {
      throw new Error("Cannot save a translation without translation ID.");
    }

    try {
      const statusCode = StatusCode.IN_REVIEW;
      const flowCode =
        translation.value.translation_type === "AUTOMATIC" ? FlowCode.AUTOTRANSLATION : FlowCode.MANUAL_TRANSLATION;

      const {
        custom_highlights = [],
        custom_important_information = [],
        custom_included = [],
        custom_non_included = [],
        premade_highlights: _premade_highlights,
        premade_important_information: _premade_important_information,
        premade_included: _premade_included,
        premade_non_included: _premade_non_included,
        lastEnglishSnapshotBeforePublication: _lastEnglishSnapshotBeforePublication,
        ...updatedTranslation
      } = translation.value;

      // V2 passes only the changed field models; legacy falls back to the full document slice.
      const highlights: Partial<allCustomHighlights> = customHighlightsOverride ?? {
        custom_highlights,
        custom_important_information,
        custom_included,
        custom_non_included,
      };

      await updateTranslation(translationId.value, {
        translation: {
          ...updatedTranslation,
          status_code: statusCode,
          flow_code: flowCode,
          to_be_translated: false,
        },
        highlights,
      });

      // remove also this publish check OFF-2547
      if (!publish) {
        // todo remove delay OFF-2547
        await waitForTimeout(updateDelayMs);
        await refreshNuxtData(distributionContentKeys.raw(translation.value.experience_id));
        translation.value.status_code = statusCode;
        translation.value.flow_code = flowCode;
      }

      modified.value = false;
    } catch (e) {
      logError("update-translation", e);
    }
  }

  async function publishTranslationExperience() {
    const { publishTranslation } = useContentCommandApi();

    await publishTranslation(translation.value.experience_id, translation.value.language_code as AvailableLanguage);

    // todo remove delay OFF-2547
    await waitForTimeout(updateDelayMs);
    await refreshNuxtData(distributionContentKeys.raw(translation.value.experience_id));

    translation.value.status_code = StatusCode.READY;
  }

  return {
    translation,
    isTranslationPublished,
    translationId,
    loaded,
    modified,
    isSaving,
    loadTranslation,
    validation,
    saveTranslation,
    publishTranslationExperience,
  };
});

// HMR
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useExperienceTranslationStore, import.meta.hot));
}
